import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import PageTitle from "../Components/PageTitle";
import axios from "../axiosInstance";
import logo from "../assets/mainLogo.jpg";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Login as LoginIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import moment from "moment";
import AdmitCardBanner from "../assets/AdmitCard.png";
import Header from "../Components/Header";

// Brand Colors
const primaryColor = '#065A79';
const secondaryColor = '#AD1C86';

const formatDate = (dateStr) => {
  return moment(dateStr, "DD/MM/YYYY").format("MMM D, YYYY");
};

const formatTime = (timeStr) => {
  if (!timeStr) return "—";
  return moment(timeStr, "HH:mm").format("h:mm A");
};

const formatDateTime = (dateStr, timeStr) => {
  if (!dateStr) return "—";
  const formattedDate = formatDate(dateStr);
  if (!timeStr) return formattedDate;
  return `${formattedDate} at ${formatTime(timeStr)}`;
};

// Function to check if end date and time has passed
const isEndDateTimePassed = (endDate, endTime) => {
  if (!endDate) return false;
  
  const endDateTime = moment(
    `${endDate} ${endTime || "23:59"}`,
    "DD/MM/YYYY HH:mm"
  );
  
  return moment().isAfter(endDateTime);
};

// Function to check if exam is active (for UI badge only)
const isExamActive = (exam) => {
  if (!exam.isActive) return false;
  if (exam.publicExam === false) return false;

  const startDateTime = moment(
    `${exam.startDate} ${exam.startTime || "00:00"}`,
    "DD/MM/YYYY HH:mm"
  );
  
  const endDateTime = moment(
    `${exam.endDate} ${exam.endTime || "23:59"}`,
    "DD/MM/YYYY HH:mm"
  );
  
  const now = moment();
  return now.isBetween(startDateTime, endDateTime, null, "[]");
};

// Function to check if exam is upcoming
const isExamUpcoming = (exam) => {
  if (!exam.isActive) return false;
  if (exam.publicExam === false) return false;
  
  const startDateTime = moment(
    `${exam.startDate} ${exam.startTime || "00:00"}`,
    "DD/MM/YYYY HH:mm"
  );
  
  return moment().isBefore(startDateTime);
};

// Function to check if admit card should be shown
const shouldShowAdmitCard = (exam) => {
  // Don't show if exam is private (publicExam = false)
  if (exam.publicExam === false) return false;
  
  // Don't show if end date & time has passed
  if (isEndDateTimePassed(exam.endDate, exam.endTime)) return false;
  
  return true;
};

// Function to convert image to base64
const getImageBase64 = (imgPath) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = () => {
      resolve(null);
    };
    img.src = imgPath;
  });
};

// Function to get negative marking display text
const getNegativeMarkingText = (negativeMarking) => {
  if (!negativeMarking || negativeMarking === 0 || negativeMarking === "0" || negativeMarking === null) {
    return "No negative marking";
  }
  return `${negativeMarking} per wrong answer`;
};

const AdmitCard = () => {
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState({ phone: "", dob: "" });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [exams, setExams] = useState([]);

  const handleVerify = async () => {
    const e = {};
    if (!/^\d{10}$/.test(credentials.phone)) e.phone = "Enter a valid 10-digit phone number";
    if (!credentials.dob) e.dob = "Date of birth is required";
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    setLoading(true);
    setApiError("");
    try {
      const response = await axios.post("/api/v1/student/verify", {
        phone: credentials.phone,
        dob: credentials.dob,
      });

      if (response.data.success) {
        const student = response.data.student || response.data.data;
        const examIds = student.examIds || [];
        
        setStudentData(student);
        setVerified(true);
        localStorage.setItem("verifiedStudent", JSON.stringify(student));

        if (examIds.length > 0) {
          const examsRes = await axios.get(`/api/v1/exam/active`);
          const allActiveExams = examsRes.data.data || [];
          
          // Filter exams: must be registered AND should show admit card
          const registeredExams = allActiveExams.filter(exam => {
            const isRegistered = examIds.includes(exam._id);
            const canShowAdmitCard = shouldShowAdmitCard(exam);
            return isRegistered && canShowAdmitCard;
          });
          
          setExams(registeredExams);
        } else {
          setExams([]);
        }
      } else {
        setApiError(response.data.message || "Verification failed.");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setApiError(err?.response?.data?.message || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const generateAdmitCard = async (exam, student) => {
    // Get logo as base64
    let logoBase64 = "";
    try {
      const base64 = await getImageBase64(logo);
      logoBase64 = base64 || "";
    } catch (error) {
      console.error("Error loading logo:", error);
    }

    const logoHTML = logoBase64 
      ? `<img src="${logoBase64}" alt="School Logo" style="width: 100%; height: 100%; object-fit: cover;" />`
      : `<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: bold;">SMS</div>`;

    // Format date of birth properly
    const formattedDOB = student.dob ? new Date(student.dob).toLocaleDateString('en-IN') : student.dob;

    const admitHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Admit Card - ${exam.name}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; background: #f5f5f5; display: flex; justify-content: center; align-items: center; min-height: 100vh; padding: 20px; }
    .card { background: #fff; width: 750px; border: 2px solid ${primaryColor}; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.15); }
    .header { background: linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%); color: white; padding: 20px 24px; display: flex; align-items: center; gap: 16px; }
    .school-logo { width: 64px; height: 64px; background: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; overflow: hidden; }
    .school-logo img { width: 100%; height: 100%; object-fit: cover; }
    .header h1 { font-size: 20px; font-weight: 700; margin: 0; }
    .header p { font-size: 13px; opacity: 0.85; margin-top: 2px; }
    .admit-label { text-align: center; background: #e8eaf6; padding: 10px; font-size: 16px; font-weight: 700; color: ${primaryColor}; letter-spacing: 2px; text-transform: uppercase; border-bottom: 1px solid #c5cae9; }
    .body { padding: 24px; }
    .section-title { font-size: 12px; font-weight: 700; color: ${secondaryColor}; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px; padding-bottom: 4px; border-bottom: 2px solid ${secondaryColor}; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 20px; }
    .field label { font-size: 11px; color: #757575; display: block; margin-bottom: 3px; }
    .field span { font-size: 14px; font-weight: 600; color: #212121; }
    .photo-box { width: 100px; height: 120px; border: 2px dashed ${primaryColor}; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 10px; color: ${primaryColor}; text-align: center; padding: 8px; background: #f5f5f5; }
    .time-info { background: #e3f2fd; border: 1px solid #90caf9; border-radius: 6px; padding: 12px; margin: 16px 0; display: flex; justify-content: space-around; }
    .time-box { text-align: center; }
    .time-box .label { font-size: 11px; color: #1565c0; font-weight: 600; text-transform: uppercase; }
    .time-box .value { font-size: 14px; font-weight: 700; color: #0d47a1; margin-top: 4px; }
    .instructions { background: #fff8e1; border: 1px solid #ffe082; border-radius: 6px; padding: 14px; margin: 16px 0; }
    .instructions h4 { font-size: 12px; color: #e65100; margin-bottom: 8px; font-weight: 700; }
    .instructions ol { padding-left: 16px; font-size: 12px; color: #5d4037; }
    .instructions li { margin-bottom: 4px; }
    .footer { background: #f3f4ff; padding: 16px 24px; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #c5cae9; }
    .signature-box { text-align: center; font-size: 11px; color: #757575; }
    .sig-line { width: 120px; border-top: 1.5px solid #333; margin: 0 auto 4px; }
    @media print { body { background: white; } .card { box-shadow: none; border: 2px solid ${primaryColor}; } }
  </style>
</head>
<body>
  <div class="card">
    <div class="header">
      <div class="school-logo">
        ${logoHTML}
      </div>
      <div>
        <h1>Shree Mangalam School</h1>
        <p>RIMC &amp; Sainik School Entrance Preparation</p>
      </div>
    </div>
    <div class="admit-label">Admit Card — Online Examination</div>
    <div class="body">
      <div style="display:flex; gap:20px; margin-bottom:20px; align-items:flex-start;">
        <div style="flex:1;">
          <div class="section-title">Student Information</div>
          <div class="grid">
            <div class="field"><label>Student Name</label><span>${student.studentName}</span></div>
            <div class="field"><label>Date of Birth</label><span>${formattedDOB}</span></div>
            <div class="field"><label>Class</label><span>${student.class || exam.class || '—'}</span></div>
            <div class="field"><label>Parent / Guardian</label><span>${student.parentName || '—'}</span></div>
            <div class="field"><label>Phone Number</label><span>${student.phone}</span></div>
            <div class="field"><label>Email</label><span>${student.email || '—'}</span></div>
          </div>
          <div class="field"><label>Address</label><span>${student.address || '—'}</span></div>
        </div>
        <div>
          <div class="photo-box"><br/>Paste<br/>Photo<br/>Here</div>
        </div>
      </div>

      <div class="section-title">Examination Details</div>
      <div class="grid">
        <div class="field"><label>Exam Name</label><span>${exam.name}</span></div>
        <div class="field"><label>Subject</label><span>${exam.subject}</span></div>
        <div class="field"><label>Class</label><span>${exam.class}</span></div>
        <div class="field"><label>Duration</label><span>${exam.duration} minutes</span></div>
        <div class="field"><label>Total Marks</label><span>${exam.totalMarks}</span></div>
        <div class="field"><label>Passing Marks</label><span>${exam.passingMarks}</span></div>
        <div class="field"><label>Negative Marking</label><span>${getNegativeMarkingText(exam.negativeMarking)}</span></div>
        <div class="field"><label>Roll No</label><span>SMS-${student._id?.slice(-6).toUpperCase()}</span></div>
        <div class="field"><label>Start Date & Time</label><span>${formatDateTime(exam.startDate, exam.startTime)}</span></div>
        <div class="field"><label>End Date & Time</label><span>${formatDateTime(exam.endDate, exam.endTime)}</span></div>
      </div>

    
      
      <div class="instructions">
        <h4>⚠️ Important Instructions</h4>
        <ol>
          <li>Carry this admit card on the day of the exam along with a valid ID proof.</li>
          <li>Please report online <strong>15 minutes before</strong> the exam start time.</li>
          <li>Log in using your registered phone number and date of birth.</li>
          <li>Ensure stable internet connection before starting the exam.</li>
          <li>Do not switch tabs or windows during the exam.</li>
          <li>The exam must be completed within the allotted time of <strong>${exam.duration} minutes</strong>.</li>
          <li>The exam will automatically end at <strong>${formatDateTime(exam.endDate, exam.endTime)}</strong>.</li>
          ${exam.negativeMarking && exam.negativeMarking !== 0 && exam.negativeMarking !== "0" && exam.negativeMarking !== null ? 
            `<li>Negative marking of <strong>${exam.negativeMarking}</strong> will apply for wrong answers.</li>` : 
            `<li>No negative marking for wrong answers.</li>`}
          <li>Read all questions carefully before answering.</li>
          <li>Once submitted, answers cannot be changed.</li>
        </ol>
      </div>
    </div>
    <div class="footer">
      <div class="signature-box">
        <div class="sig-line"></div>
        Student Signature
      </div>
      <div style="text-align:center; font-size:11px; color:#757575;">
        <div style="font-weight:700; font-size:13px; color:${primaryColor};">Shree Mangalam School</div>
        <div>This is a computer-generated admit card</div>
        <div>Generated on: ${new Date().toLocaleDateString("en-IN")} at ${new Date().toLocaleTimeString("en-IN")}</div>
      </div>
      <div class="signature-box">
        <div class="sig-line"></div>
        Principal's Signature
      </div>
    </div>
  </div>
  <script>window.onload = () => window.print();</script>
</body>
</html>
    `;
    const blob = new Blob([admitHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `AdmitCard_${student.studentName.replace(/\s+/g, "_")}_${exam.name.replace(/\s+/g, "_")}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async (exam) => {
    if (!studentData) return;
    await generateAdmitCard(exam, studentData);
  };

  return (
    <>
      <Navbar />
      <PageTitle
        title="Admit Card - Shree Mangalam School"
        description="View and download your exam admit card."
        url="/admit-card"
      />
      <Header image={AdmitCardBanner} alt={"Shree Mangalam School"} />

      <Box sx={{ maxWidth: 900, mx: "auto", px: { xs: 2, md: 4 }, py: 5 }}>
        <Typography variant="h4" fontWeight={700} mb={2} sx={{ color: primaryColor }}>
          Admit Card
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Login with your registered phone number and date of birth to view and download your admit card.
        </Typography>

        {!verified ? (
          <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2} sx={{ color: primaryColor }}>
                Student Verification
              </Typography>
              <Alert severity="info" sx={{ mb: 2, bgcolor: '#e3f2fd' }}>
                Enter your registered phone number and date of birth to access your admit cards.
              </Alert>

              {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Registered Phone Number"
                    name="phone"
                    value={credentials.phone}
                    onChange={(e) => {
                      setCredentials((p) => ({ ...p, phone: e.target.value }));
                      setErrors((p) => ({ ...p, phone: "" }));
                      setApiError("");
                    }}
                    fullWidth
                    inputProps={{ maxLength: 10 }}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    placeholder="10-digit mobile number"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    value={credentials.dob}
                    onChange={(e) => {
                      setCredentials((p) => ({ ...p, dob: e.target.value }));
                      setErrors((p) => ({ ...p, dob: "" }));
                      setApiError("");
                    }}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.dob}
                    helperText={errors.dob}
                  />
                </Grid>
              </Grid>

              <Button
                variant="contained"
                fullWidth
                sx={{ 
                  mt: 3, 
                  borderRadius: 2, 
                  textTransform: "none", 
                  fontWeight: 600, 
                  py: 1.4, 
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                  "&:hover": { 
                    background: `linear-gradient(135deg, ${secondaryColor} 0%, ${primaryColor} 100%)`
                  } 
                }}
                onClick={handleVerify}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LoginIcon />}
              >
                {loading ? "Verifying..." : "Verify & View Admit Cards"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={3}>
            <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: primaryColor }}>
                    👤 Student Details
                  </Typography>
                  <Chip 
                    label={`Roll: SMS-${studentData._id?.slice(-6).toUpperCase()}`} 
                    sx={{ bgcolor: primaryColor, color: 'white' }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {studentData.studentName} • {studentData.class || 'Class Not Assigned'} • {studentData.phone}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" fontWeight={700} sx={{ color: primaryColor }}>
                  Your Admit Cards
                </Typography>

                {exams.length === 0 ? (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    No admit cards available. Either you are not registered for any exam, or the exams you registered for have ended or are private.
                  </Alert>
                ) : (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {exams.map((exam) => {
                      const active = isExamActive(exam);
                      const upcoming = isExamUpcoming(exam);
                      const endDatePassed = isEndDateTimePassed(exam.endDate, exam.endTime);
                      
                      return (
                        <Grid item xs={12} sm={6} key={exam._id}>
                          <Card sx={{ height: "100%", position: 'relative' }}>
                            <CardContent>
                              {active && !endDatePassed && (
                                <Chip
                                  icon={<EventAvailableIcon />}
                                  label="Active"
                                  color="success"
                                  size="small"
                                  sx={{ position: 'absolute', top: 10, right: 10 }}
                                />
                              )}
                              {upcoming && !endDatePassed && (
                                <Chip
                                  icon={<AccessTimeIcon />}
                                  label="Upcoming"
                                  color="warning"
                                  size="small"
                                  sx={{ position: 'absolute', top: 10, right: 10 }}
                                />
                              )}
                              {endDatePassed && (
                                <Chip
                                  icon={<EventBusyIcon />}
                                  label="Ended"
                                  color="default"
                                  size="small"
                                  sx={{ position: 'absolute', top: 10, right: 10 }}
                                />
                              )}
                              <Typography variant="subtitle1" fontWeight={700} sx={{ pr: 8 }}>
                                {exam.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" mb={1}>
                                {exam.subject} • Class {exam.class}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Start: {formatDateTime(exam.startDate, exam.startTime)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                End: {formatDateTime(exam.endDate, exam.endTime)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                Duration: {exam.duration} mins • Total Marks: {exam.totalMarks}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                                Passing Marks: {exam.passingMarks} • Negative: {getNegativeMarkingText(exam.negativeMarking)}
                              </Typography>
                              <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                fullWidth
                                sx={{ 
                                  mt: 1, 
                                  borderRadius: 2, 
                                  textTransform: "none",
                                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                                  '&:hover': {
                                    background: `linear-gradient(135deg, ${secondaryColor} 0%, ${primaryColor} 100%)`
                                  }
                                }}
                                onClick={() => handleDownload(exam)}
                              >
                                Download Admit Card
                              </Button>
                              {endDatePassed && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                                  * Exam has ended - Admit card for reference only
                                </Typography>
                              )}
                              {upcoming && !endDatePassed && (
                                <Typography variant="caption" color="info" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                                  * Exam will start on {formatDateTime(exam.startDate, exam.startTime)}
                                </Typography>
                              )}
                              {active && !endDatePassed && (
                                <Typography variant="caption" color="success" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                                  * Exam is active now - You can take the exam
                                </Typography>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>

            <Button
              variant="text"
              onClick={() => navigate("/online-exams")}
              sx={{ 
                alignSelf: "flex-start",
                color: secondaryColor,
                '&:hover': { color: primaryColor }
              }}
            >
              Register for Another Exam
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => {
                setVerified(false);
                setStudentData(null);
                setExams([]);
                setCredentials({ phone: "", dob: "" });
              }}
              sx={{ 
                alignSelf: "flex-start",
                borderColor: primaryColor,
                color: primaryColor
              }}
            >
              Search Another Student
            </Button>
          </Stack>
        )}
      </Box>
      <Footer />
    </>
  );
};

export default AdmitCard;